import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useClientAuth } from "../../contexts/ClientAuthContext";
import { createSignedAssetUrl, fetchClientProjectById } from "../../services/clientProjectService";
import "./client.css";

export default function ProjectDetail() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { isSupabaseReady } = useClientAuth();

    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [downloadError, setDownloadError] = useState("");
    const [downloadingFileId, setDownloadingFileId] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadProject = async () => {
            setIsLoading(true);
            setError("");
            try {
                const data = await fetchClientProjectById(projectId);
                if (isMounted) {
                    setProject(data);
                    if (!data) {
                        setError("Proje bulunamadı veya erişim izniniz yok.");
                    }
                }
            } catch (loadError) {
                if (isMounted) {
                    setError(loadError.message ?? "Proje detayları yüklenirken hata oluştu.");
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadProject();

        return () => {
            isMounted = false;
        };
    }, [projectId]);

    const handleDownload = async (file) => {
        setDownloadError("");

        if (!file.storagePath) {
            setDownloadError("Bu dosya demo modunda gösteriliyor. İndirme bağlantısı mevcut değil.");
            return;
        }

        try {
            setDownloadingFileId(file.id);
            const url = await createSignedAssetUrl({ bucket: file.storageBucket, path: file.storagePath });
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
        } catch (downloadErr) {
            setDownloadError(downloadErr.message ?? "Dosya indirilirken hata oluştu.");
        } finally {
            setDownloadingFileId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="client-project-view">
                <div className="client-empty">Proje detayları yükleniyor...</div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="client-project-view">
                <div className="client-empty">{error || "Proje bulunamadı."}</div>
                <button
                    className="client-project-file__download"
                    type="button"
                    onClick={() => navigate("/client/dashboard")}
                >
                    Proje listesine dön
                </button>
            </div>
        );
    }

    return (
        <div className="client-project-view">
            <header className="client-project-view__header">
                <div>
                    <span className="client-project-view__status">{project.status || "Durum belirtilmedi"}</span>
                    <h1 className="client-project-view__title">{project.title}</h1>
                </div>
                <button
                    className="client-project-file__download"
                    type="button"
                    onClick={() => navigate("/client/dashboard")}
                >
                    ← Projelerim
                </button>
            </header>

            {project.summary && <p className="client-project-view__summary">{project.summary}</p>}

            <section className="client-project-view__section">
                <h3>Takvim</h3>
                <div className="client-project-card__dates">
                    {project.kickoffDate && <span>Kickoff: {project.kickoffDate}</span>}
                    {project.deliveryDate && <span>Teslim: {project.deliveryDate}</span>}
                    {project.updatedAt && <span>Son güncelleme: {new Date(project.updatedAt).toLocaleString()}</span>}
                </div>
            </section>

            <section className="client-project-view__section">
                <h3>Dosyalar</h3>
                {downloadError && <p className="client-form__error">{downloadError}</p>}
                <div className="client-project-files">
                    {project.files.length === 0 ? (
                        <div className="client-empty">Bu projeye ait paylaşılmış bir dosya bulunmuyor.</div>
                    ) : (
                        project.files.map((file) => (
                            <div key={file.id} className="client-project-file">
                                <div className="client-project-file__meta">
                                    <span>{file.label}</span>
                                    <span>
                                        {file.note || ""}
                                        {file.sizeLabel ? ` • ${file.sizeLabel}` : ""}
                                        {file.fileType ? ` • ${file.fileType}` : ""}
                                    </span>
                                </div>
                                <button
                                    className="client-project-file__download"
                                    type="button"
                                    disabled={!isSupabaseReady || !file.storagePath || downloadingFileId === file.id}
                                    onClick={() => handleDownload(file)}
                                >
                                    {downloadingFileId === file.id
                                        ? "Link hazırlanıyor..."
                                        : file.storagePath
                                            ? "İndir"
                                            : "İndirme yok"}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </section>

            <section className="client-project-view__section">
                <h3>Güncellemeler</h3>
                <div className="client-project-updates">
                    {project.updates.length === 0 ? (
                        <div className="client-empty">Henüz bir statü güncellemesi paylaşılmadı.</div>
                    ) : (
                        project.updates.map((update) => (
                            <article key={update.id} className="client-project-update">
                                <h4>{update.title}</h4>
                                {update.status && <span className="client-project-update__meta">Durum: {update.status}</span>}
                                {update.postedAt && (
                                    <span className="client-project-update__meta">
                                        Tarih: {new Date(update.postedAt).toLocaleString()}
                                    </span>
                                )}
                                {update.body && <p>{update.body}</p>}
                            </article>
                        ))
                    )}
                </div>
            </section>
        </div>
    );
}
